<template>
  <header
    :class="{'-opend':isOpen}"
    class="main-header">
    <div class="fixed-container">
      <p class="catchphrase" :class="[lang.toLowerCase()]" v-html="$t('header.text001')" />
      <div class="header-buttons">
        <button class="menu-button" @click="clickMenu(isOpen)">
          <img :src="btnMenu"  :alt="$t('header.menu')" />
        </button>
        <h1 class="game-title">
          <img
            :src="gameTitle"
            :alt="$t('header.title001')+$t('header.title002')"
          />
        </h1>
        <div class="right-area">
          <button class="sound-button" @click="clickSound(isPlaying)" v-if="isShowSoundButton">
            <img :src="btnSound" alt="sound" />
          </button>
        </div>
      </div>
    </div>
    <menus class="main-menu"/>
  </header>
</template>

<script>
import { mapMutations, mapGetters } from 'vuex'
import Menus from '@components/the-menu.vue'
import localeCommon from '@locales/common'
import { languages, getLocaleKey, isIE } from '@constants'

export default {
  name: 'mainHeader',
  $messages: localeCommon,
  components: {
    Menus,
  },
  computed: {
    ...mapGetters('menu', ['isOpen']),
    ...mapGetters('bgm', ['isPlaying', 'currentBgm']),
    lang() {
      let lang
      if (languages.enums.find(l => l.value === this.$route.params.lang)){
        lang = this.$route.params.lang
      } else {
        lang = languages.get(getLocaleKey()).value
      }
      return lang
    },
    isShowSoundButton() {
      if (isIE()) return false
      const excludeList = ['about']
      return excludeList.every((item) => {
        return this.$route.name !== item
      })
    },
    btnMenu() {
      return this.isOpen ? require(`@images/the-header/${this.lang}/btn_menu-close.png`) : require(`@images/the-header/${this.lang}/btn_menu-open.png`)
    },
    gameTitle() {
      return require(`@images/the-header/${this.lang}/img_logo.png`)
    },
    btnSound() {
      return this.isPlaying ? require(`@images/the-header/btn_sound-off.png`) : require(`@images/the-header/btn_sound-on.png`)
    },
    audioExtension() {
      const audioObj = new Audio()
      let extension
      if (audioObj.canPlayType('audio/mp4') === 'maybe') {
        extension ='aac'
      }
      if (audioObj.canPlayType('audio/aac') === 'maybe') {
        extension = 'm4a'
      }
      return extension
    },
    audioObj() {
      let audio = null
      let path = null
      if (/\/series/.test(this.$route.path)) {
        const seriesId = this.$route.name === 'series' ? Number(this.$route.query.series) : Number(this.$route.params.seriesId)
        const exceptionSeries = [113, 115, 116]
        if (exceptionSeries.includes(seriesId)){
          //NOTE: seriesID-113 はbgmデータがないのでmainbgmで代用
          //NOTE: seriesID-115,116 はjp zh とそれ以外で音源を分ける
          const lang = this.lang === 'ja-JP' || this.lang === 'zh-TW' ? 'jp' : 'en'
          path = seriesId !== 113 ? `/sounds/${this.audioExtension}/bgm_${seriesId}_${lang}.${this.audioExtension}` : `/sounds/${this.audioExtension}/bgm_main_${lang}.${this.audioExtension}`
        } else {
          path = `/sounds/${this.audioExtension}/bgm_${seriesId}.${this.audioExtension}`
        }
      } else {
        const lang = this.lang === 'ja-JP' || this.lang === 'zh-TW' ? 'jp' : 'en'
        path = `/sounds/${this.audioExtension}/bgm_main_${lang}.${this.audioExtension}`
      } 
      audio = new Audio(path)
      return audio
    },
  },
  created() {
    document.addEventListener('visibilitychange', this.handleVisibilityChange)
  },
  watch: {
    '$route'() {
      if (this.isOpen) {
        this.setMenuState(false)
      }
      if (this.$route.name === 'about' && this.currentBgm && this.isPlaying ) {
        this.bgmStop()
      }
    },
    '$route.query.series'(value) {
      if (value && this.isPlaying){
        this.bgmStop()
        this.bgmPlay()
      }
    }
  },
  methods: {
    ...mapMutations('menu', ['setMenuState']),
    ...mapMutations('bgm', ['setIsPlaying', 'setCurrentBgm']),
    clickMenu(state) {
      this.setMenuState(!state)
    },
    clickSound(state) {
      if (state) {
        this.bgmStop()
      } else {
        this.bgmPlay()
      }
    },
    bgmPlay() {
      this.setCurrentBgm(this.audioObj)
      this.currentBgm.loop = true
      this.currentBgm.play()
      this.setIsPlaying(true)
    },
    bgmStop(option) {
      if (!this.currentBgm) return
      this.currentBgm.pause()
      if (!option || option.resetTime) {
        this.currentBgm.currentTime = 0
      }
      const state = option && option.isPlaying ? option.isPlaying : false
      this.setIsPlaying(state)
    },
    handleVisibilityChange(){
      if (!this.isPlaying) return
      if (document.hidden) {
        this.bgmStop({resetTime: false, isPlaying: true})
      } else {
        this.bgmPlay()
      }
    },
  },
  destroyed(){
    window.removeEventListener('visibilitychange', this.handleVisibilityChange)
  },
}
</script>

<style lang="scss" scoped>
.main-header {
  position: relative;
  z-index: 99;
  .fixed-container {
    position: absolute;
    width: 640px;
    overflow: hidden;
    top: 0;
    left: 0;
    right: 0;
    margin: auto;
    z-index: 3;
  }
  .catchphrase {
    background-color: $color-black;
    text-align: center;
    line-height: 32px;
    position: relative;
    z-index: 99;
    &.de-de {
      font-size: 14px;
    }
    &.en-gb,
    &.en-us,
    &.fr-ca,
    &.fr-fr {
      font-size: 16px;
    }
    &.es-es,
    &.es-mx,
    &.it-it,
    &.pt-br {
      font-size: 12px;
    }
  }
  .header-buttons {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    background-image: url(#{$img-path}the-header/bg_header.png);
    background-repeat: no-repeat;
    background-position: center -2px;
    height: 180px;
    position: relative;
    z-index: 99;
    > .game-title {
      margin: 18px 0 0 -30px;

    }
  }
  .main-menu {
    display: block;
    top: -1477px;
    transform: translate(0px, -1477px);
    transition-delay: 0ms;
    transition-duration: 400ms;
    transition-timing-function: ease-in-out;
    position: absolute;
    left: 0;
    bottom: 0;
    padding-top: 214px;
  }
  &.-opend {
    position: absolute;
    width: 100%;
    top: 0;
    bottom: 0;
    .fixed-container {
      position: fixed;
    }
    .main-menu {
      transform: translate(0px, 1477px);
    }
  }
}
.menu-button {
  background-color: transparent;
  width: 142px;
  height: 143px;
  margin: 6px 0 0 -15px;
  padding: 0;
  @include tap-event();
  img {
    display: block;
  }
}
.right-area {
  width: 92px;
}
.sound-button {
  background-color: transparent;
  width: 92px;
  height: 92px;
  margin: 29px 14px 0 -20px;
  padding: 0;
  @include tap-event();
  img {
    display: block;
    width: 100%;
  }
}
</style>
